import { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router';
import {
    Box, Typography, Button, Stack, Chip, CircularProgress,
    Input, Select, Option, IconButton, Avatar, Divider,
} from '@mui/joy';
import { motion } from 'framer-motion';

import ArrowBackRoundedIcon              from '@mui/icons-material/ArrowBackRounded';
import WorkRoundedIcon                   from '@mui/icons-material/WorkRounded';
import DownloadRoundedIcon               from '@mui/icons-material/DownloadRounded';
import AccountBalanceWalletRoundedIcon   from '@mui/icons-material/AccountBalanceWalletRounded';
import EmojiEventsRoundedIcon            from '@mui/icons-material/EmojiEventsRounded';
import RemoveCircleOutlineRoundedIcon    from '@mui/icons-material/RemoveCircleOutlineRounded';
import PaidRoundedIcon                   from '@mui/icons-material/PaidRounded';
import ExpandMoreRoundedIcon             from '@mui/icons-material/ExpandMoreRounded';
import ExpandLessRoundedIcon             from '@mui/icons-material/ExpandLessRounded';

import Header from '../../components/Header';
import { AppContext } from '../../context';
import { APIGetReport, APIGetLocations, getToken, API_URL } from '../../api';
import MemeLoader from '../../components/MemeLoader';
import { businessTodayISO, businessToday, businessDayISO } from '../../businessDay';

// ─── Утилиты ──────────────────────────────────────────────────────────────────
const toISO = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const fmtMoney = (val) => {
    const n = parseFloat(val) || 0;
    return n.toLocaleString('ru-RU', { maximumFractionDigits: 0 });
};

const getInitials = (name = '') => {
    const parts = name.trim().split(' ');
    return (parts[0]?.[0] || '') + (parts[1]?.[0] || '');
};

const getMonthStart = () => {
    const now = businessToday();   // деловой «сегодня» (до 06:00 — ещё вчера)
    return toISO(new Date(now.getFullYear(), now.getMonth(), 1));
};

const getPeriods = () => {
    const now = businessToday();               // деловой день, а не календарный
    const today = businessTodayISO();
    const yesterday = businessDayISO(-1);
    const weekStart = new Date(now);
    const day = weekStart.getDay();
    weekStart.setDate(weekStart.getDate() - (day === 0 ? 6 : day - 1));
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    return [
        { key: 'today',     label: 'Сегодня', from: today,             to: today },
        { key: 'yesterday', label: 'Вчера',   from: yesterday,         to: yesterday },
        { key: 'week',      label: 'Неделя',  from: toISO(weekStart),  to: today },
        { key: 'month',     label: 'Месяц',   from: toISO(monthStart), to: today },
    ];
};

// ─── Анимации ─────────────────────────────────────────────────────────────────
const pageVariants = {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.38, ease: [0.22, 1, 0.36, 1] } },
    exit:    { opacity: 0, y: -12, transition: { duration: 0.22 } },
};

const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.05 } } };

const fadeUp = {
    hidden:  { opacity: 0, y: 14 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.32, ease: [0.22, 1, 0.36, 1] } },
};


// ─── Карточка точки внутри роли ──────────────────────────────────────
function LocationRow({ loc, role }) {
    // role: 'photographer' | 'administrator'
    const total    = parseFloat(role === 'photographer' ? loc.total : loc.subordinate_total) || 0;
    const salary   = parseFloat(loc.salary) || 0;
    const base     = parseFloat(loc.base_salary) || 0;      // оклад (фикс)
    const revenue  = parseFloat(loc.revenue_salary) || 0;   // % с выручки
    const reward   = parseFloat(loc.reward) || 0;
    const daily    = parseFloat(loc.daily_reward) || 0;
    const fines    = parseFloat(loc.fines) || 0;
    const deduct   = parseFloat(loc.deductions) || 0;
    const net      = parseFloat(loc.net_pay) || 0;

    return (
        <Box sx={{
            bgcolor: 'var(--joy-palette-background-level2)',
            border: '1px solid var(--joy-palette-divider)',
            borderRadius: 'var(--joy-radius-md)',
            p: 1.5, mb: 1,
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                    {loc.location_name}
                </Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 800, color: net >= 0 ? '#22c55e' : 'var(--joy-palette-danger-400)', fontVariantNumeric: 'tabular-nums' }}>
                    {net >= 0 ? '' : '−'}{fmtMoney(Math.abs(net))} ₽
                </Typography>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.5, fontSize: 10 }}>
                <Box>
                    <Typography sx={{ fontSize: 9, color: 'var(--joy-palette-text-tertiary)' }}>Выручка</Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--joy-palette-primary-300)' }}>{fmtMoney(total)}</Typography>
                </Box>
                <Box>
                    <Typography sx={{ fontSize: 9, color: 'var(--joy-palette-text-tertiary)' }}>ЗП</Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#818cf8' }}>{fmtMoney(salary)}</Typography>
                </Box>
                {role === 'administrator' && (
                    <>
                        <Box>
                            <Typography sx={{ fontSize: 9, color: 'var(--joy-palette-text-tertiary)' }}>Оклад</Typography>
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#a78bfa' }}>{fmtMoney(base)}</Typography>
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: 9, color: 'var(--joy-palette-text-tertiary)' }}>% выручки</Typography>
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#a78bfa' }}>{fmtMoney(revenue)}</Typography>
                        </Box>
                    </>
                )}
                <Box>
                    <Typography sx={{ fontSize: 9, color: 'var(--joy-palette-text-tertiary)' }}>Премии</Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#22c55e' }}>{fmtMoney(reward + daily)}</Typography>
                </Box>
                <Box>
                    <Typography sx={{ fontSize: 9, color: 'var(--joy-palette-text-tertiary)' }}>Дни</Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--joy-palette-text-secondary)' }}>{loc.work_days || 0}</Typography>
                </Box>
                {fines > 0 && (
                    <Box>
                        <Typography sx={{ fontSize: 9, color: 'var(--joy-palette-text-tertiary)' }}>Штрафы</Typography>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--joy-palette-danger-400)' }}>
                            −{fmtMoney(fines)}
                        </Typography>
                    </Box>
                )}
                {deduct > 0 && (
                    <Box>
                        <Typography sx={{ fontSize: 9, color: 'var(--joy-palette-text-tertiary)' }}>Списания</Typography>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#f59e0b' }}>
                            −{fmtMoney(deduct)}
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
}

// ─── Секция роли (фотограф / админ) ──────────────────────────────────
function RoleSection({ title, role, data, accent }) {
    // Защита от случайных дублей по location_id + стабильная сортировка по выручке.
    // Хук должен быть ВЫШЕ early return — иначе нарушаются rules-of-hooks.
    const locations = useMemo(() => {
        const map = new Map();
        for (const loc of (data?.by_location || [])) {
            if (!map.has(loc.location_id)) map.set(loc.location_id, loc);
        }
        return Array.from(map.values()).sort((a, b) => {
            const ta = parseFloat(role === 'photographer' ? a.total : a.subordinate_total) || 0;
            const tb = parseFloat(role === 'photographer' ? b.total : b.subordinate_total) || 0;
            return tb - ta;
        });
    }, [data?.by_location, role]);

    if (!data) return null;
    const total = parseFloat(role === 'photographer' ? data.total : data.subordinate_total) || 0;
    const net   = parseFloat(data.net_pay) || 0;

    return (
        <Box sx={{ mt: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 4, height: 18, bgcolor: accent, borderRadius: 1 }} />
                    <Typography sx={{ fontSize: 12, fontWeight: 800, color: 'var(--joy-palette-text-primary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {title}
                    </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                    <Typography sx={{ fontSize: 9, color: 'var(--joy-palette-text-tertiary)' }}>
                        Выручка {fmtMoney(total)} · К выплате
                    </Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 800, color: net >= 0 ? '#22c55e' : 'var(--joy-palette-danger-400)', fontVariantNumeric: 'tabular-nums' }}>
                        {net >= 0 ? '' : '−'}{fmtMoney(Math.abs(net))} ₽
                    </Typography>
                </Box>
            </Box>
            {locations.map(loc => (
                <LocationRow key={`${role}-${loc.location_id}`} loc={loc} role={role} />
            ))}
        </Box>
    );
}

// ─── Карточка сотрудника ─────────────────────────────────────────────
function SalaryCard({ data, rank }) {
    const [expanded, setExpanded] = useState(false);

    const ph = data.as_photographer;
    const ad = data.as_administrator;
    const combinedNet = parseFloat(data.combined_net_pay) || 0;

    // Бейджи ролей
    const roleBadges = [];
    if (ph) roleBadges.push({ label: 'Фотограф', color: '#22c55e' });
    if (ad) roleBadges.push({ label: 'Админ',    color: '#818cf8' });

    return (
        <Box sx={{
            bgcolor: 'var(--joy-palette-background-level1)',
            border: '1px solid var(--joy-palette-divider)',
            borderRadius: 'var(--joy-radius-xl)',
            overflow: 'hidden', transition: 'border-color .15s',
            '&:hover': { borderColor: 'var(--joy-palette-primary-500)' },
        }}>
            {/* Шапка */}
            <Box onClick={() => setExpanded(p => !p)}
                sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5, cursor: 'pointer' }}>
                <Avatar src={data.employee_avatar_url} size="sm" sx={{
                    width: 36, height: 36, fontSize: 12, fontWeight: 700,
                    bgcolor: rank < 3 ? 'rgba(162,38,226,0.2)' : 'var(--joy-palette-background-level3)',
                    color: rank < 3 ? 'var(--joy-palette-primary-300)' : 'var(--joy-palette-text-tertiary)',
                    border: `1.5px solid ${rank < 3 ? 'var(--joy-palette-primary-500)' : 'var(--joy-palette-divider)'}`,
                    flexShrink: 0,
                }}>
                    {getInitials(data.employee_name)}
                </Avatar>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                        {data.employee_name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.6, mt: 0.4, flexWrap: 'wrap' }}>
                        {roleBadges.map(b => (
                            <Chip key={b.label} size="sm" variant="soft"
                                sx={{ bgcolor: `${b.color}1a`, color: b.color, fontSize: 9, fontWeight: 700, '--Chip-paddingInline': '6px', '--Chip-minHeight': '18px' }}>
                                {b.label}
                            </Chip>
                        ))}
                    </Box>
                </Box>

                <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                    <Typography sx={{
                        fontSize: 18, fontWeight: 800, fontVariantNumeric: 'tabular-nums',
                        color: combinedNet >= 0 ? '#22c55e' : 'var(--joy-palette-danger-400)',
                    }}>
                        {combinedNet >= 0 ? '' : '−'}{fmtMoney(Math.abs(combinedNet))}
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>
                        итого к выплате
                    </Typography>
                </Box>

                {expanded
                    ? <ExpandLessRoundedIcon sx={{ fontSize: 20, color: 'var(--joy-palette-text-tertiary)', flexShrink: 0 }} />
                    : <ExpandMoreRoundedIcon sx={{ fontSize: 20, color: 'var(--joy-palette-text-tertiary)', flexShrink: 0 }} />}
            </Box>

            {/* Детали */}
            {expanded && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} transition={{ duration: 0.25 }}>
                    <Box sx={{ px: 2, pb: 2 }}>
                        <Divider sx={{ mb: 1 }} />
                        <RoleSection title="Как фотограф"   role="photographer"   data={ph} accent="#22c55e" />
                        <RoleSection title="Как администратор" role="administrator" data={ad} accent="#818cf8" />
                    </Box>
                </motion.div>
            )}
        </Box>
    );
}


// ════════════════════════════════════════════════════════════════════
//  SalaryPage — детальный расчёт ЗП
// ════════════════════════════════════════════════════════════════════
export default function SalaryPage() {
    const { token, showToast, onRefreshTick, hasPerm } = useContext(AppContext);
    const navigate = useNavigate();
    const periods = useMemo(getPeriods, []);

    const canExcel = hasPerm('reports.excel');

    const [report,       setReport]       = useState(null);
    const [locations,    setLocations]    = useState([]);
    const [loading,      setLoading]      = useState(true);
    const [downloading,  setDownloading]  = useState(false);
    const [activePeriod, setActivePeriod] = useState('month');
    const [dateFrom,     setDateFrom]     = useState(getMonthStart);
    const [dateTo,       setDateTo]       = useState(() => businessTodayISO());
    const [locationId,   setLocationId]   = useState('');

    const loadReport = useCallback(async () => {
        setLoading(true);
        const params = { date_from: dateFrom, date_to: dateTo };
        if (locationId) params.location_id = locationId;
        const data = await APIGetReport(token, params);
        if (data && !data.error) setReport(data);
        setLoading(false);
    }, [token, dateFrom, dateTo, locationId]);

    useEffect(() => {
        (async () => {
            const locs = await APIGetLocations(token);
            if (Array.isArray(locs)) setLocations(locs);
        })();
    }, [token]);

    useEffect(() => { loadReport(); }, [loadReport]);
    useEffect(() => onRefreshTick(loadReport), [onRefreshTick, loadReport]);

    const selectPeriod = (p) => { setActivePeriod(p.key); setDateFrom(p.from); setDateTo(p.to); };

    const handleExcel = async () => {
        setDownloading(true);
        try {
            const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
            if (locationId) params.append('location_id', locationId);
            const resp = await fetch(`${API_URL}/api/finance/reports/excel/?${params}`, { headers: { Authorization: `Token ${getToken()}` } });
            if (!resp.ok) throw new Error();
            const blob = await resp.blob();
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = `salary_${dateFrom}_${dateTo}.xlsx`;
            a.click();
            URL.revokeObjectURL(a.href);
            showToast('Отчёт скачан');
        } catch { showToast('Ошибка скачивания', 'danger'); }
        setDownloading(false);
    };

    const employees = report?.employees || [];
    const totals    = report?.totals || {};

    // Сводные итоги: тащим прямо из ответа API
    const salaryTotals = useMemo(() => ({
        salary:     parseFloat(totals.ph_salary || 0) + parseFloat(totals.admin_salary || 0),
        reward:     parseFloat(totals.ph_reward || 0) + parseFloat(totals.ph_daily_reward || 0) + parseFloat(totals.admin_reward || 0) + parseFloat(totals.admin_daily_reward || 0),
        fines:      parseFloat(totals.ph_fines || 0) + parseFloat(totals.admin_fines || 0),
        deductions: parseFloat(totals.ph_deductions || 0) + parseFloat(totals.admin_deductions || 0),
        netPay:     parseFloat(totals.combined_net_pay || 0),
    }), [totals]);

    return (
        <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit"
            style={{ minHeight: '100vh', background: 'var(--joy-palette-background-body)' }}>
            <Header />

            <Box sx={{ maxWidth: 900, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>

                {/* ── Шапка ──────────────────────────────────────── */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <IconButton variant="plain" onClick={() => navigate('/manager/stats')}
                                sx={{ color: 'var(--joy-palette-text-secondary)' }}>
                                <ArrowBackRoundedIcon />
                            </IconButton>
                            <Box>
                                <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--joy-palette-text-primary)' }}>
                                    Зарплаты
                                </Typography>
                                <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)', mt: 0.3 }}>
                                    Детальный расчёт ЗП, премий и штрафов
                                </Typography>
                            </Box>
                        </Box>
                        {canExcel && (
                            <Button size="sm" variant="soft" color="primary"
                                startDecorator={<DownloadRoundedIcon sx={{ fontSize: 16 }} />}
                                loading={downloading} onClick={handleExcel}
                                sx={{ fontWeight: 700, fontSize: 12 }}
                            >
                                Excel
                            </Button>
                        )}
                    </Box>
                </motion.div>

                {/* ── Фильтры ──────────────────────────────────── */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.04 }}>
                    <Box sx={{
                        bgcolor: 'var(--joy-palette-background-level1)',
                        border: '1px solid var(--joy-palette-divider)',
                        borderRadius: 'var(--joy-radius-xl)',
                        p: { xs: 1.5, sm: 2 }, mb: 2.5,
                    }}>
                        <Box sx={{ display: 'flex', gap: 0.8, mb: 1.5 }}>
                            {periods.map(p => {
                                const active = activePeriod === p.key;
                                return (
                                    <Chip key={p.key} size="md"
                                        variant={active ? 'solid' : 'outlined'}
                                        color={active ? 'primary' : 'neutral'}
                                        onClick={() => selectPeriod(p)}
                                        sx={{
                                            flex: 1, justifyContent: 'center',
                                            cursor: 'pointer', fontWeight: 700, fontSize: 13,
                                            bgcolor: active ? p.bg : 'transparent',
                                            color: active ? p.color : 'var(--joy-palette-text-tertiary)',
                                            borderColor: active ? p.color : 'var(--joy-palette-divider)',
                                            transition: 'all .15s',
                                            '&:hover': { borderColor: p.color },
                                        }}
                                    >
                                        {p.label}
                                    </Chip>
                                );
                            })}
                        </Box>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr' }, gap: 1 }}>
                            <Input type="date" size="sm" value={dateFrom}
                                onChange={(e) => { setDateFrom(e.target.value); setActivePeriod(''); }}
                            />
                            <Input type="date" size="sm" value={dateTo}
                                onChange={(e) => { setDateTo(e.target.value); setActivePeriod(''); }}
                            />
                            <Select size="sm" value={locationId} onChange={(_, v) => setLocationId(v || '')}
                                placeholder="Все точки"
                            >
                                <Option value="">Все точки</Option>
                                {locations.map(l => <Option key={l.id} value={l.id}>{l.name}</Option>)}
                            </Select>
                        </Box>
                    </Box>
                </motion.div>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <MemeLoader />
                    </Box>
                ) : (
                    <motion.div variants={stagger} initial="hidden" animate="visible">
                        <Stack spacing={2.5}>

                            {/* ── Сводные карточки ────────────────── */}
                            <motion.div variants={fadeUp}>
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(5,1fr)' }, gap: 1 }}>
                                    {[
                                        { label: 'ЗП',        value: salaryTotals.salary,     color: '#818cf8', bg: 'rgba(129,140,248,0.08)', icon: <PaidRoundedIcon sx={{ fontSize: 16 }} /> },
                                        { label: 'Премии',    value: salaryTotals.reward,     color: '#22c55e', bg: 'rgba(34,197,94,0.08)', icon: <EmojiEventsRoundedIcon sx={{ fontSize: 16 }} /> },
                                        { label: 'Штрафы',    value: salaryTotals.fines,      color: 'var(--joy-palette-danger-400)', bg: 'rgba(248,113,113,0.08)', icon: <RemoveCircleOutlineRoundedIcon sx={{ fontSize: 16 }} /> },
                                        { label: 'Списания',  value: salaryTotals.deductions, color: '#f59e0b', bg: 'rgba(245,158,11,0.08)', icon: <RemoveCircleOutlineRoundedIcon sx={{ fontSize: 16 }} /> },
                                        { label: 'К выплате', value: salaryTotals.netPay,     color: 'var(--joy-palette-primary-400)', bg: 'rgba(162,38,226,0.10)', icon: <WorkRoundedIcon sx={{ fontSize: 16 }} />, highlight: true },
                                    ].map(s => (
                                        <Box key={s.label} sx={{
                                            px: 1.5, py: 1.4,
                                            borderRadius: 'var(--joy-radius-lg)',
                                            border: `1px solid ${s.highlight ? 'var(--joy-palette-primary-500)' : 'var(--joy-palette-divider)'}`,
                                            bgcolor: s.bg,
                                            gridColumn: s.highlight ? { xs: '1 / -1', sm: 'auto' } : 'auto',
                                        }}>
                                            <Box sx={{ color: s.color, mb: 0.3 }}>{s.icon}</Box>
                                            <Typography sx={{ fontSize: s.highlight ? 22 : 18, fontWeight: 800, color: s.color, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                                                {fmtMoney(s.value)}
                                            </Typography>
                                            <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-secondary)', mt: 0.3 }}>{s.label}</Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </motion.div>

                            {/* ── Карточки фотографов ─────────────── */}
                            {employees.length === 0 ? (
                                <motion.div variants={fadeUp}>
                                    <Box sx={{ textAlign: 'center', py: 6 }}>
                                        <WorkRoundedIcon sx={{ fontSize: 48, color: 'var(--joy-palette-text-tertiary)', mb: 1 }} />
                                        <Typography sx={{ fontSize: 14, color: 'var(--joy-palette-text-tertiary)' }}>
                                            Нет данных за выбранный период
                                        </Typography>
                                    </Box>
                                </motion.div>
                            ) : (
                                employees.map((emp, idx) => (
                                    <motion.div key={emp.employee_id} variants={fadeUp}>
                                        <SalaryCard data={emp} rank={idx} />
                                    </motion.div>
                                ))
                            )}
                        </Stack>
                    </motion.div>
                )}
            </Box>
        </motion.div>
    );
}